/**
 * Assignment Users Tab Component
 * Shows user progress data grid
 */

import { useCallback } from 'react'
import { Box, Typography, Chip, LinearProgress } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import dayjs from 'dayjs'

interface UsersTabProps {
  userProgress: any[]
}

export default function UsersTab({ userProgress }: UsersTabProps) {
  let UsersTabToolbar = useCallback(
    () => <DataGridToolbar fileName="assignment-users" />,
    [],
  )

  // Helper function to format status
  let formatStatus = (status: string) => {
    switch (status) {
      case 'NotStarted':
        return 'Not Started'
      case 'InProgress':
        return 'In Progress'
      case 'Completed':
        return 'Completed'
      default:
        return status
    }
  }

  // Define columns for the DataGrid
  let userGridColumns: GridColDef[] = [
    {
      field: 'userName',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'completedCourses',
      headerName: 'Courses',
      width: 120,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.row.completedCourses}/{params.row.totalCourses}
          </Typography>
        </Box>
      ),
      sortComparator: (v1, v2, param1: any, param2: any) => {
        let ratio1 = param1?.row
          ? param1.row.completedCourses / param1.row.totalCourses
          : 0
        let ratio2 = param2?.row
          ? param2.row.completedCourses / param2.row.totalCourses
          : 0
        return ratio1 - ratio2
      },
    },
    {
      field: 'progressPercentage',
      headerName: 'Progress',
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            minWidth: 120,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2">{Math.round(params.value)}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={params.value}
            sx={{ height: 6, borderRadius: 3 }}
            color={
              params.value === 100
                ? 'success'
                : params.value > 50
                  ? 'primary'
                  : 'warning'
            }
          />
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            size="small"
            label={formatStatus(params.value)}
            color={
              params.value === 'Completed'
                ? 'success'
                : params.value === 'InProgress'
                  ? 'primary'
                  : 'default'
            }
          />
        </Box>
      ),
    },
    {
      field: 'lastActivityDate',
      headerName: 'Last Activity',
      width: 150,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
        >
          {params.value ? dayjs(params.value).fromNow() : 'Never'}
        </Typography>
      ),
    },
  ]

  // Prepare user data for the grid
  let userGridRows = userProgress.map((user) => ({
    id: user.userID,
    userID: user.userID,
    userName: user.userName,
    email: user.email,
    progressPercentage: user.progressPercentage,
    status: user.status,
    completedCourses: user.completedCourses,
    totalCourses: user.totalCourses,
    lastActivityDate: user.lastActivityDate,
  }))

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        User Progress ({userProgress.length} loaded)
      </Typography>
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGridPremium
          rows={userGridRows}
          columns={userGridColumns}
          slots={{ toolbar: UsersTabToolbar }}
          showToolbar
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
          }}
        />
      </Box>
    </Box>
  )
}
